import { useId, useCallback, forwardRef } from 'react'
import { useTranslation } from 'next-i18next'
import styled from '@emotion/styled'
import { errorStyles, inputStyles } from 'css/utils'
import { InputProps } from 'modules/ui/atoms/Input'
import { InputContainer } from 'modules/ui/atoms/Input/InputContainer'

export const StyledTextArea = styled.textarea(
  {
    minHeight: '48px',
    resize: 'vertical',
    verticalAlign: 'top',
    ...inputStyles,
  },
  errorStyles,
)

export const TextArea = forwardRef<HTMLTextAreaElement, InputProps>((props, ref) => {
  const { t } = useTranslation()

  const {
    onChange,
    onBlur,
    onFocus,
    value,
    placeholder,
    error,
    maxLength,
    tabIndex,
    autoFocus,
    dataId,
    hasOverflow = true,
  } = props

  const id = useId()
  const captionId = useId()

  // Only show error if hasOverflow is true (allows going over limit)
  const maxLengthError = hasOverflow && maxLength && value && value.length > maxLength
  let maxLengthErrorText = ''
  let maxLengthReachedText = ''

  if (maxLengthError) {
    const extraCharacters = value.length - maxLength

    maxLengthErrorText = t('TextArea.overLimit', {
      extraCharacters,
      defaultValue: `You are {{extraCharacters}} character${
        extraCharacters > 1 ? 's' : ''
      } over the limit.`,
    })
  }

  // Show "limit reached" message when hasOverflow is false and at limit
  if (!hasOverflow && maxLength && value && value.length >= maxLength) {
    maxLengthReachedText = t('TextArea.limitReached', {
      maxLength,
      defaultValue: `Maximum length of {{maxLength}} characters reached.`,
    })
  }

  const onChangeCallback = useCallback(
    (e: { target: { value: string } }) => {
      const newValue = e.target.value

      // If hasOverflow is false, prevent input beyond maxLength
      if (!hasOverflow && maxLength && newValue.length > maxLength) {
        return // Don't call onChange, effectively blocking the input
      }

      onChange(newValue)
    },
    [onChange, hasOverflow, maxLength],
  )

  // Determine what error/message to show
  const displayError = maxLengthError ? maxLengthErrorText : error
  const displayMessage = !hasOverflow && maxLengthReachedText ? maxLengthReachedText : displayError

  return (
    <InputContainer {...props} id={id} captionId={captionId} error={displayMessage}>
      <StyledTextArea
        errored={!!error || !!maxLengthError}
        onChange={onChangeCallback}
        onBlur={onBlur}
        onFocus={onFocus}
        ref={ref}
        value={value}
        placeholder={placeholder}
        id={id}
        aria-describedby={captionId}
        aria-invalid={!!error ? 'true' : 'false'}
        tabIndex={tabIndex}
        autoFocus={autoFocus}
        data-id={dataId}
        maxLength={hasOverflow ? undefined : maxLength} // Block input when hasOverflow is false
      />
    </InputContainer>
  )
})

TextArea.displayName = 'TextArea'
